import React, { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, Maximize2, RotateCcw } from "lucide-react";

const MIN_ZOOM = 40;
const MAX_ZOOM = 150;
const ZOOM_STEP = 10;
const FALLBACK_HEIGHT = 600;

// Sandboxed HTML preview iframe — the same technique used by
// EmailTemplatePreview.jsx, extracted so both the read-only list preview
// and the wizard's live editing preview share one implementation.
// Includes zoom controls so a tall template can be scaled down to fit
// the viewport without scrolling.
const EmailHtmlPreviewFrame = ({ html, emptyLabel = "Nothing to preview yet." }) => {
  const [zoom, setZoom] = useState(100);
  const [contentHeight, setContentHeight] = useState(FALLBACK_HEIGHT);
  const containerRef = useRef(null);
  const iframeRef = useRef(null);

  // Reset to a neutral zoom whenever the underlying HTML changes (new template / edit).
  useEffect(() => {
    setZoom(100);
  }, [html]);

  const measureContentHeight = () => {
    const doc = iframeRef.current?.contentDocument;
    const height = doc?.documentElement
      ? Math.max(doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0)
      : 0;
    if (height > 0) setContentHeight(height);
    return height;
  };

  const fitToScreen = () => {
    const container = containerRef.current;
    const height = measureContentHeight();
    const availableHeight = container?.clientHeight;
    if (!height || !availableHeight) return;

    const fitted = Math.floor((availableHeight / height) * 100);
    setZoom(Math.max(MIN_ZOOM, Math.min(100, fitted)));
  };

  if (!html) {
    return (
      <div className="flex items-center justify-center h-full py-16 text-text_muted text-sm">
        {emptyLabel}
      </div>
    );
  }

  const zoomIn = () => setZoom((z) => Math.min(MAX_ZOOM, z + ZOOM_STEP));
  const zoomOut = () => setZoom((z) => Math.max(MIN_ZOOM, z - ZOOM_STEP));
  const resetZoom = () => setZoom(100);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-b border-border bg-surface flex-shrink-0">
        <span className="text-[11px] font-mono text-text_muted">{zoom}%</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={zoomOut}
            disabled={zoom <= MIN_ZOOM}
            title="Zoom out"
            className="p-1 rounded-md text-text_secondary bg-surface_alt border border-border disabled:opacity-40"
          >
            <ZoomOut size={13} />
          </button>
          <button
            type="button"
            onClick={zoomIn}
            disabled={zoom >= MAX_ZOOM}
            title="Zoom in"
            className="p-1 rounded-md text-text_secondary bg-surface_alt border border-border disabled:opacity-40"
          >
            <ZoomIn size={13} />
          </button>
          <button
            type="button"
            onClick={fitToScreen}
            title="Fit to screen"
            className="p-1 rounded-md text-text_secondary bg-surface_alt border border-border"
          >
            <Maximize2 size={13} />
          </button>
          <button
            type="button"
            onClick={resetZoom}
            title="Reset zoom"
            className="p-1 rounded-md text-text_secondary bg-surface_alt border border-border"
          >
            <RotateCcw size={13} />
          </button>
        </div>
      </div>

      <div ref={containerRef} className="flex-1 overflow-auto bg-surface_alt">
        <div style={{ width: "100%", height: `${(contentHeight * zoom) / 100}px` }}>
          <iframe
            ref={iframeRef}
            srcDoc={html}
            title="Email Preview"
            className="border-0"
            sandbox="allow-same-origin"
            onLoad={measureContentHeight}
            style={{
              width: `${(10000 / zoom).toFixed(2)}%`,
              height: `${contentHeight}px`,
              transform: `scale(${zoom / 100})`,
              transformOrigin: "top left",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default EmailHtmlPreviewFrame;
